import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { sanitizeSearchInput, sanitizeString } from '../utils/security';
import TechServiceExcelExportModal from '../components/TechServiceExcelExportModal';
import {
  fetchTechServices,
  createTechService,
  updateTechService,
  fetchTechServicesSelect,
  invalidateTechServicesSelectCache,
  searchTechServices,
  fetchKonsinyeCariBulk,
} from '../services/techServicesService';
import KonsinyeCariLimitCard from '../components/KonsinyeCariLimitCard';
import { fetchCities, fetchDistrictsByCity } from '../services/locationsService';
import {
  fetchServiceAreasByTechService,
  deleteServiceArea,
} from '../services/serviceAreasService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from '../components/TableListFooter';
import AktifSayiKart from '../components/AktifSayiKart';
import { ROWS_PER_PAGE_OPTIONS } from '../constants/pagination';

const rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS;

const safeText = (value) => sanitizeString(value ?? '');

// İl ve ilçe isimlerini al (backend objesi varsa onu kullan, yoksa state'ten al)
const getCityName = (techService, cityNames) => {
  if (techService.il && typeof techService.il === 'object') {
    return techService.il.name || techService.il.ad || '';
  }
  return cityNames[String(techService.cityId)] || '';
};

const getDistrictName = (techService, districtNames) => {
  if (techService.ilce && typeof techService.ilce === 'object') {
    return techService.ilce.name || techService.ilce.ad || '';
  }
  return districtNames[String(techService.districtId)] || '';
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;

  if (error?.status === 404) return 'API endpoint bulunamadı.';
  if (error?.status === 401) return 'Kimlik doğrulama hatası. Lütfen tekrar giriş yapın.';
  if (error?.status === 403) return 'Bu işlem için yetkiniz yok.';

  let message =
    error?.message ||
    error?.data?.mesaj ||
    error?.data?.message ||
    error?.data?.error ||
    error?.data?.hata ||
    fallback;

  if (message && typeof message === 'string') {
    message = message
      .replace(/&#x27;/g, "'")
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>');
  }

  if (message === 'Failed to fetch') {
    return 'Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol edin.';
  }

  return message || fallback;
};

const TechServiceManagementPage = () => {
  const { confirm } = useConfirmDialog();
  const navigate = useNavigate();
  const [techServices, setTechServices] = useState([]);
  const [filters, setFilters] = useState({ search: '', cityId: 'all', status: 'active' });
  const [activeStatusTab, setActiveStatusTab] = useState('active'); // 'active' veya 'passive'
  const [searchInput, setSearchInput] = useState(''); // Arama input'u için ayrı state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [aktifSayisi, setAktifSayisi] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isQuickModalOpen, setIsQuickModalOpen] = useState(false);
  const [quickForm, setQuickForm] = useState({ ad: '', cityId: '', districtId: '' });
  const [quickError, setQuickError] = useState('');
  const [quickSubmitting, setQuickSubmitting] = useState(false);
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [ilcelerLoading, setIlcelerLoading] = useState(false);
  const [cityNames, setCityNames] = useState({});
  const [districtNames, setDistrictNames] = useState({});
  /** Varsayılan: teknik servis adı A→Z (`sortBy=ad`, `sortOrder=asc`); sütun tıklanınca değişir */
  const [listSort, setListSort] = useState({ by: 'ad', order: 'asc' });
  const [konsinyeCariBulk, setKonsinyeCariBulk] = useState({});
  const [konsinyeCariBulkLoading, setKonsinyeCariBulkLoading] = useState(false);
  const loadRequestSeqRef = useRef(0);
  const paginationMetaRef = useRef(paginationMeta);
  paginationMetaRef.current = paginationMeta;

  const sanitizedSearchValue = useMemo(() => {
    const sanitized = sanitizeSearchInput(filters.search);
    return sanitized && sanitized.trim().length > 0 ? sanitized : undefined;
  }, [filters.search]);

  const excelFilterSummary = useMemo(() => {
    const sekme = activeStatusTab === 'active' ? 'Aktif servisler' : 'Pasif servisler';
    const ilLabel =
      filters.cityId !== 'all'
        ? iller.find((il) => String(il.id ?? il._id ?? '') === String(filters.cityId))?.name || `İl #${filters.cityId}`
        : 'Tüm iller';
    const parts = [sekme, `İl: ${ilLabel}`];
    if (sanitizedSearchValue && sanitizedSearchValue.trim().length >= 3) {
      parts.push(`Arama: ${sanitizedSearchValue.trim()}`);
    }
    return parts.join(' | ');
  }, [activeStatusTab, filters.cityId, sanitizedSearchValue, iller]);

  const excelExportArgs = useMemo(
    () => ({
      aktif: activeStatusTab === 'active',
      cityId: filters.cityId,
      searchText: sanitizedSearchValue && sanitizedSearchValue.trim().length >= 3 ? sanitizedSearchValue : '',
      sortBy: listSort.by === 'il' ? 'il' : 'ad',
      sortOrder: listSort.order === 'desc' ? 'desc' : 'asc',
    }),
    [activeStatusTab, filters.cityId, sanitizedSearchValue, listSort.by, listSort.order]
  );
  const filterParams = useMemo(
    () => ({
      cityId: filters.cityId === 'all' ? undefined : parseInt(filters.cityId, 10),
      status: activeStatusTab === 'active' ? 'active' : 'passive',
      search: sanitizedSearchValue,
    }),
    [filters.cityId, activeStatusTab, sanitizedSearchValue]
  );

  const loadTechServices = useCallback(async () => {
    const requestedPage = currentPage;
    const reqId = ++loadRequestSeqRef.current;
    setLoading(true);
    setError('');
    const sortApi =
      listSort.by === 'ad' || listSort.by === 'il'
        ? { sortBy: listSort.by, sortOrder: listSort.order }
        : {};
    try {
      if (filterParams.search && filterParams.search.trim().length >= 3) {
        const response = await searchTechServices(filterParams.search, {
          cityId: filterParams.cityId,
          aktif: activeStatusTab === 'active',
          sayfa: requestedPage,
          limit: rowsPerPage,
          ...sortApi,
        });
        if (reqId !== loadRequestSeqRef.current) return;
        const list = response?.veri?.teknikServisler || [];
        const pagination = response?.veri?.sayfalama || {};
        const veri = response?.veri || {};
        const totalVal = pagination.toplamTeknikServis ?? veri?.toplam ?? list.length;
        const totalPg =
          pagination.toplamSayfa ??
          veri?.toplamSayfa ??
          (Math.ceil(totalVal / rowsPerPage) || 1);

        setTechServices(list);
        setPaginationMeta({ total: totalVal, totalPages: totalPg });
        if (veri?.aktifSayisi != null) setAktifSayisi(veri.aktifSayisi);
        if (requestedPage > totalPg && totalPg > 0) {
          setCurrentPage(totalPg);
        }
      } else {
        const response = await fetchTechServices({
          page: requestedPage,
          limit: rowsPerPage,
          cityId: filterParams.cityId,
          aktif: activeStatusTab === 'active',
          ...sortApi,
        });
        if (reqId !== loadRequestSeqRef.current) return;
        const list = response?.veri?.teknikServisler || [];
        const pagination = response?.veri?.sayfalama || {};
        const veri = response?.veri || {};
        const totalVal = pagination.toplamTeknikServis ?? veri?.toplam ?? list.length;
        const totalPg =
          pagination.toplamSayfa ??
          veri?.toplamSayfa ??
          (Math.ceil(totalVal / rowsPerPage) || 1);

        setTechServices(list);
        setPaginationMeta({ total: totalVal, totalPages: totalPg });
        if (veri?.aktifSayisi != null) setAktifSayisi(veri.aktifSayisi);
        if (requestedPage > totalPg && totalPg > 0) {
          setCurrentPage(totalPg);
        }
      }
    } catch (err) {
      if (reqId !== loadRequestSeqRef.current) return;
      setError(getErrorMessage(err, 'Teknik servisler yüklenemedi'));
    } finally {
      if (reqId === loadRequestSeqRef.current) {
        setLoading(false);
        setSearchLoading(false);
      }
    }
  }, [currentPage, rowsPerPage, filterParams, activeStatusTab, listSort.by, listSort.order]);

  useEffect(() => {
    loadTechServices();
  }, [loadTechServices]);

  useEffect(() => {
    const ids = techServices.map((ts) => ts.id || ts._id).filter(Boolean);
    if (ids.length === 0) {
      setKonsinyeCariBulk({});
      return undefined;
    }
    let cancelled = false;
    setKonsinyeCariBulkLoading(true);
    fetchKonsinyeCariBulk(ids)
      .then((res) => {
        if (!cancelled && res?.basarili) {
          setKonsinyeCariBulk(res.veri || {});
        }
      })
      .catch(() => {
        if (!cancelled) setKonsinyeCariBulk({});
      })
      .finally(() => {
        if (!cancelled) setKonsinyeCariBulkLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [techServices]);

  useEffect(() => {
    const loadIller = async () => {
      try {
        const response = await fetchCities();
        if (response?.basarili && Array.isArray(response.veri?.iller)) {
          setIller(response.veri.iller);
        }
      } catch (err) {
        console.error('İller yüklenemedi:', err);
        if (window.showNotification) {
          window.showNotification('error', 'İller yüklenemedi.');
        }
      }
    };
    loadIller();
  }, []);

  useEffect(() => {
    if (iller.length === 0) return;
    const map = {};
    iller.forEach((il) => {
      const id = il?.id || il?._id;
      if (id) {
        map[String(id)] = il.name || il.ad || '—';
      }
    });
    setCityNames(map);
  }, [iller]);


  useEffect(() => {
    const loadIlceler = async () => {
      if (!quickForm.cityId || quickForm.cityId.trim() === '') {
        console.log('[TechServiceManagementPage] No cityId, clearing districts');
        setIlceler([]);
        return;
      }
      
      const cityIdNum = parseInt(quickForm.cityId, 10);
      if (isNaN(cityIdNum)) {
        console.warn('[TechServiceManagementPage] Invalid cityId:', quickForm.cityId);
        setIlceler([]);
        return;
      }
      
      console.log('[TechServiceManagementPage] Loading districts for cityId:', cityIdNum);
      setIlcelerLoading(true);
      try {
        const response = await fetchDistrictsByCity(cityIdNum);
        console.log('[TechServiceManagementPage] fetchDistrictsByCity response:', response);
        console.log('[TechServiceManagementPage] response.veri?.ilceler:', response?.veri?.ilceler);
        console.log('[TechServiceManagementPage] response.veri?.districts:', response?.veri?.districts);
        
        // Farklı response formatlarını kontrol et
        let list = response?.veri?.ilceler || response?.veri?.districts || response?.veri?.ilceList || response?.veri || [];
        
        // Eğer list bir array değilse, kontrol et
        if (!Array.isArray(list)) {
          console.warn('[TechServiceManagementPage] Districts list is not an array:', list);
          list = [];
        }
        
        // API formatı: { id: number, adi: string, cityId: number }
        const serializedList = list.map((ilce) => {
          return {
            id: ilce.id || ilce._id,
            adi: ilce.adi || ilce.ad || ilce.name,
            ad: ilce.adi || ilce.ad || ilce.name, // Geriye dönük uyumluluk için
            name: ilce.name || ilce.adi || ilce.ad, // Geriye dönük uyumluluk için
            cityId: ilce.cityId
          };
        });
        
        console.log('[TechServiceManagementPage] Districts list (final, serialized):', serializedList);
        console.log('[TechServiceManagementPage] Districts list length:', serializedList.length);
        
        setIlceler(serializedList);
      } catch (err) {
        console.error('[TechServiceManagementPage] İlçeler yüklenemedi:', err);
        setIlceler([]);
      } finally {
        setIlcelerLoading(false);
      }
    };
    loadIlceler();
  }, [quickForm.cityId]);

  const fetchedDistrictCitiesRef = React.useRef(new Set());
  useEffect(() => {
    const uniqueCityIds = Array.from(
      new Set(techServices.map((service) => service.cityId).filter(Boolean))
    );
    uniqueCityIds.forEach((cityId) => {
      if (fetchedDistrictCitiesRef.current.has(cityId)) return;
      fetchedDistrictCitiesRef.current.add(cityId);

      fetchDistrictsByCity(parseInt(cityId, 10))
        .then((response) => {
          if (response?.basarili && Array.isArray(response.veri?.ilceler)) {
            setDistrictNames((prev) => {
              const copy = { ...prev };
              response.veri.ilceler.forEach((ilce) => {
                const id = ilce?.id || ilce?._id;
                if (id) {
                  copy[String(id)] = ilce.name || ilce.ad || '—';
                }
              });
              return copy;
            });
          }
        })
        .catch((err) => {
          console.error('İlçe isimleri alınamadı:', err);
        });
    });
  }, [techServices]);

  const handleQuickChange = (event) => {
    const { name, value } = event.target;
    setQuickForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'cityId' ? { districtId: '' } : {}),
    }));
  };

  const handleQuickSubmit = async (event) => {
    event.preventDefault();
    setQuickError('');
    if (!quickForm.ad.trim() || !quickForm.cityId || !quickForm.districtId) {
      setQuickError('Teknik servis adı, il ve ilçe zorunludur.');
      return;
    }

    const payload = {
      ad: quickForm.ad.trim(),
      cityId: parseInt(quickForm.cityId, 10),
      districtId: parseInt(quickForm.districtId, 10),
    };

    setQuickSubmitting(true);
    try {
      const duplicate = await fetchTechServicesSelect();
      const normalized = quickForm.ad.trim().toLowerCase();
      const existing = (duplicate?.veri?.teknikServisler || []).find((service) => {
        const serviceName = (service.ad || '').trim().toLowerCase();
        return serviceName && serviceName === normalized;
      });
      if (existing) {
        const dupLocation = existing?.il?.name || 'ilgili şehir';
        setQuickError(
          `Bu isim zaten kayıtlı (${dupLocation}). Öneri: "${quickForm.ad.trim()} ${dupLocation}" gibi farklılaştırın.`
        );
        return;
      }

      const response = await createTechService(payload);

      if (response?.basarili === false) {
        throw new Error(response?.mesaj || response?.message || 'Teknik servis oluşturulamadı');
      }

      const created = response?.veri?.teknikServis || response?.veri || {};
      const newId = created?.id || created?._id || created?.techServiceId;
      if (newId) {
        navigate(`/tech-services/${newId}/edit`, { replace: true });
      } else {
        navigate('/tech-services/add', { replace: true });
      }

      setIsQuickModalOpen(false);
      setQuickForm({ ad: '', cityId: '', districtId: '' });

      invalidateTechServicesSelectCache();

      if (window.showNotification) {
        window.showNotification('success', 'Teknik servis oluşturuldu; formdan devam edebilirsiniz.');
      }
    } catch (error) {
      console.error('Quick create error:', error);
      setQuickError(getErrorMessage(error, 'Teknik servis oluşturulamadı'));
    } finally {
      setQuickSubmitting(false);
    }
  };

  const handleDelete = async (techService) => {
    const techServiceId = techService.id || techService._id;
    const serviceName = techService.ad || 'Teknik servis';
    const isActive = techService.aktif !== false; // aktif true veya undefined ise aktif kabul et

    if (isActive) {
      // Aktif servis: Pasif yap ve yetki alanlarını sil
      const confirmed = await confirm({
        title: 'Teknik servisi pasif yap',
        message: `${serviceName} adlı teknik servis pasif yapılacak ve tüm yetki alanları silinecek. Onaylıyor musunuz?`,
        variant: 'warning',
        confirmLabel: 'Evet',
      });
      if (!confirmed) return;

      try {
        // 1. Önce tüm yetki alanlarını sil
        try {
          const serviceAreasResponse = await fetchServiceAreasByTechService(techServiceId);
          if (serviceAreasResponse?.basarili && serviceAreasResponse?.veri?.yetkiAlanlari) {
            const serviceAreas = serviceAreasResponse.veri.yetkiAlanlari;
            if (serviceAreas && serviceAreas.length > 0) {
              const deletePromises = serviceAreas.map(area => {
                const areaId = area.id || area._id;
                if (areaId) {
                  return deleteServiceArea(areaId);
                }
                return Promise.resolve();
              });
              await Promise.all(deletePromises);
              console.log(`[handleDelete] ${serviceAreas.length} yetki alanı silindi`);
            }
          }
        } catch (areaError) {
          console.warn('Yetki alanları silinirken hata:', areaError);
          // Yetki alanları silinemese bile devam et
        }

        // 2. Teknik servisi pasif yap
        const payload = { aktif: false };
        const response = await updateTechService(techServiceId, payload);

        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Teknik servis pasif yapılamadı');
        }

        invalidateTechServicesSelectCache();

        if (window.showNotification) {
          window.showNotification('success', 'Teknik servis pasif yapıldı ve tüm yetki alanları silindi');
        }
        loadTechServices();
      } catch (err) {
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Teknik servis pasif yapılamadı'));
        }
      }
    } else {
      // Pasif servis: Aktif yap
      const confirmed = await confirm({
        title: 'Teknik servisi aktif yap',
        message: `${serviceName} adlı teknik servis aktif yapılacak. Onaylıyor musunuz?`,
        variant: 'info',
        confirmLabel: 'Evet',
      });
      if (!confirmed) return;

      try {
        const payload = { aktif: true };
        const response = await updateTechService(techServiceId, payload);

        if (response?.basarili === false) {
          throw new Error(response?.mesaj || response?.message || 'Teknik servis aktif yapılamadı');
        }

        invalidateTechServicesSelectCache();

        if (window.showNotification) {
          window.showNotification('success', 'Teknik servis aktif yapıldı');
        }
        loadTechServices();
      } catch (err) {
        if (window.showNotification) {
          window.showNotification('error', getErrorMessage(err, 'Teknik servis aktif yapılamadı'));
        }
      }
    }
  };

  const handleSearchChange = (event) => {
    const value = sanitizeSearchInput(event.target.value, { trimEdges: false });
    setSearchInput(value);
  };

  const handleCityFilterChange = (event) => {
    const cityId = event.target.value;
    setFilters((prev) => ({
      ...prev,
      cityId,
    }));
    setCurrentPage(1);
  };

  // Debounce için search input - en az 3 karakter gerekiyor
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // En az 3 karakter girilmişse arama yap
      if (searchInput.trim().length >= 3) {
        console.log('[TechServiceManagementPage] Search input changed (>=3 chars):', searchInput);
        setSearchLoading(true);
        setFilters((prev) => {
          const newFilters = { ...prev, search: searchInput.trim() };
          console.log('[TechServiceManagementPage] Filters updated:', newFilters);
          return newFilters;
        });
        setCurrentPage(1);
      } else if (searchInput.trim().length === 0) {
        // Arama terimi temizlendiyse, filtreyi de temizle
        setSearchLoading(false);
        setFilters((prev) => {
          const newFilters = { ...prev, search: '' };
          return newFilters;
        });
        setCurrentPage(1);
      } else {
        setSearchLoading(false);
      }
    }, 1000); // 1000ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (rowsPerPageOptions.includes(value)) {
      setRowsPerPage(value);
      setCurrentPage(1);
    }
  };


  const handleSortHeaderClick = (by) => {
    setListSort((prev) => {
      if (prev.by === by) {
        return { by, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { by, order: 'asc' };
    });
    setCurrentPage(1);
  };

  const sortHeaderIcon = (by) => {
    if (listSort.by !== by) {
      return <i className="fas fa-sort" style={{ marginLeft: 6, opacity: 0.45 }} aria-hidden />;
    }
    return listSort.order === 'asc' ? (
      <i className="fas fa-sort-up" style={{ marginLeft: 6 }} aria-hidden />
    ) : (
      <i className="fas fa-sort-down" style={{ marginLeft: 6 }} aria-hidden />
    );
  };

  const renderTechServicesTable = () => {
    if (loading) {
      return (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div className="loading-text">Teknik servisler yükleniyor</div>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      );
    }
    if (error) return <div className="error-state">{safeText(error)}</div>;
    if (techServices.length === 0) {
      return (
        <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
          <i className="fas fa-search" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
          <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Kayıt bulunamadı</div>
        </div>
      );
    }

    return (
      <table className="admins-table">
        <thead>
          <tr>
            <th>
              <button
                type="button"
                className="table-sort-header"
                onClick={() => handleSortHeaderClick('ad')}
                aria-sort={listSort.by === 'ad' ? (listSort.order === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                Teknik Servis Adı
                {sortHeaderIcon('ad')}
              </button>
            </th>
            <th>
              <button
                type="button"
                className="table-sort-header"
                onClick={() => handleSortHeaderClick('il')}
                aria-sort={listSort.by === 'il' ? (listSort.order === 'asc' ? 'ascending' : 'descending') : 'none'}
              >
                İl / İlçe
                {sortHeaderIcon('il')}
              </button>
            </th>
            <th>Telefon</th>
            <th>E-posta</th>
            <th>Konsinye Cari</th>
            <th className="islemler-sutunu">İşlemler</th>
          </tr>
        </thead>
        <tbody>
          {techServices.map((techService) => (
            <tr key={techService.id || techService._id}>
              <td>
                <span
                  className="tech-service-name-link"
                  onClick={() =>
                    navigate(`/tech-services/${techService.id || techService._id}/edit`, {
                      state: {
                        draft: {
                          ad: techService.ad,
                          cityId: techService.cityId ? String(techService.cityId) : '',
                          districtId: techService.districtId ? String(techService.districtId) : '',
                        },
                      },
                    })
                  }
                >
                  {techService.ad}
                  {techService.isTest === true && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#92400e',
                        background: '#fef3c7',
                        padding: '2px 6px',
                        borderRadius: 4,
                      }}
                    >
                      Test
                    </span>
                  )}
                </span>
              </td>
              <td>
                {(() => {
                  const cityName = getCityName(techService, cityNames) || (techService.cityId ? String(techService.cityId) : '');
                  const districtName = getDistrictName(techService, districtNames) || (techService.districtId ? String(techService.districtId) : '');
                  const parts = [];
                  if (cityName) parts.push(cityName);
                  if (districtName) parts.push(districtName);
                  return parts.length > 0 ? parts.join(' / ') : '—';
                })()}
              </td>
              <td>{techService.telefon || '—'}</td>
              <td>{techService.mail || '—'}</td>
              <td>
                <KonsinyeCariLimitCard
                  compact
                  techServiceId={techService.id || techService._id}
                  ozet={konsinyeCariBulk[String(techService.id || techService._id)]}
                  loading={konsinyeCariBulkLoading}
                />
              </td>
              <td className="table-actions islemler-sutunu">
                {techService.aktif !== false ? (
                  <button
                    type="button"
                    className="category-action-btn delete-btn"
                    aria-label="Teknik servisi pasif yap"
                    onClick={() => handleDelete(techService)}
                    title="Pasif yap ve yetki alanlarını sil"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                ) : (
                  <button
                    type="button"
                    className="category-action-btn move-btn"
                    aria-label="Teknik servisi aktif yap"
                    onClick={() => handleDelete(techService)}
                    title="Aktif yap"
                  >
                    <i className="fas fa-check"></i>
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const totalRecords = paginationMeta.total || techServices.length;

  return (
    <main className="main-content tech-service-management">
      <div className="content-header">
        <h1>Teknik Servis Yönetimi</h1>
      </div>

      <AktifSayiKart label="Aktif teknik servis" value={aktifSayisi} icon="fas fa-tools" />

      <div className="status-tabs">
        <button
          type="button"
          className={`status-tab-btn ${activeStatusTab === 'active' ? 'active' : ''}`}
          onClick={() => {
            setActiveStatusTab('active');
            setCurrentPage(1);
          }}
        >
          Aktif Servisler
        </button>
        <button
          type="button"
          className={`status-tab-btn ${activeStatusTab === 'passive' ? 'active' : ''}`}
          onClick={() => {
            setActiveStatusTab('passive');
            setCurrentPage(1);
          }}
        >
          Pasif Servisler
        </button>
      </div>

      <div className="toolbar">
        <div className="search-filter-bar">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Ad, telefon veya e-posta ile ara (min 3 karakter)..."
              value={searchInput}
              onChange={handleSearchChange}
              maxLength={100}
              autoComplete="off"
            />
            {searchLoading && (
              <i className="fas fa-spinner fa-spin" style={{ right: '10px' }} aria-hidden />
            )}
          </div>
          <div className={`role-filter-dropdown ${filters.cityId !== 'all' ? 'filter-active' : ''}`}>
            <i className="fas fa-map-marker-alt role-filter-icon"></i>
            <select value={filters.cityId} onChange={handleCityFilterChange} aria-label="İl filtreleme">
              <option value="all">Tüm iller</option>
              {iller.map((il) => (
                <option key={il.id} value={String(il.id)}>
                  {il.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="toolbar-end-actions">
          <button type="button" className="secondary-btn" onClick={() => setIsExportModalOpen(true)}>
            <i className="fas fa-file-excel"></i>
            <span>Excel’e aktar</span>
          </button>
          <button type="button" className="primary-btn add-user-btn" onClick={() => setIsQuickModalOpen(true)}>
            <i className="fas fa-plus"></i>
            <span>Teknik Servis Ekle</span>
          </button>
        </div>
      </div>

      <div className="table-container">{renderTechServicesTable()}</div>

      <TableListFooter
        totalRecords={totalRecords}
        currentPage={currentPage}
        totalPages={paginationMeta.totalPages || 1}
        onPageChange={setCurrentPage}
        loading={loading}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        onRowsPerPageChange={(value) => handleRowsPerPageChange({ target: { value: String(value) } })}
      />

      <TechServiceExcelExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportArgs={excelExportArgs}
        filterSummary={excelFilterSummary}
      />

      {isQuickModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={() => setIsQuickModalOpen(false)}>
          <div className="modal quick-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Teknik Servis Ekle</h2>
              <button type="button" className="close-btn" onClick={() => setIsQuickModalOpen(false)} aria-label="Kapat">
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form className="modal-body" onSubmit={handleQuickSubmit}>
              <div className="form-grid">
                <label className="form-field">
                  <span>Teknik Servis Adı *</span>
                  <input type="text" name="ad" value={quickForm.ad} onChange={handleQuickChange} maxLength={100} required />
                </label>
              </div>
              <div className="inline-select-row">
                <label className="form-field">
                  <span>İl *</span>
                  <select name="cityId" value={quickForm.cityId} onChange={handleQuickChange} required>
                    <option value="">İl Seçiniz</option>
                    {iller.map((il) => (
                      <option key={il.id || il._id} value={String(il.id || il._id)}>
                        {il.name || il.ad}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-field">
                  <span>İlçe *</span>
                  <select
                    name="districtId"
                    value={quickForm.districtId}
                    onChange={handleQuickChange}
                    required
                    disabled={!quickForm.cityId || ilcelerLoading}
                  >
                    <option value="">İlçe Seçiniz</option>
                    {ilceler.map((ilce) => {
                      const ilceId = ilce.id || ilce._id;
                      const ilceName = ilce.adi || ilce.ad || ilce.name || '';
                      return (
                        <option key={ilceId} value={String(ilceId)}>
                          {ilceName}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>
              {quickError && <div className="error-state small">{quickError}</div>}
              <div className="modal-footer">
                <button type="button" className="secondary-btn" onClick={() => setIsQuickModalOpen(false)}>
                  İptal
                </button>
                <button type="submit" className="primary-btn" disabled={quickSubmitting}>
                  {quickSubmitting ? 'İlerleniyor...' : 'Devam'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default TechServiceManagementPage;
